
import { useState } from "react"
import { useOvertimeBase } from "./useOvertimeBase"
import { useOvertimeRequests } from "./useOvertimeRequests"
import { useApprovals } from "./useApprovals"
import { useDataRetrieval } from "./useDataRetrieval"
import { useAnalyticsData } from "./useAnalyticsData"

export const useOvertimeData = () => {
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  
  // Use the base hook for data fetching and state management
  const { 
    overtimeRequests, 
    setOvertimeRequests, 
    employees, 
    refreshRequests, 
    user 
  } = useOvertimeBase()
  
  // Initialize shifts as empty array since useOvertimeBase doesn't provide it
  const shifts: Record<string, unknown>[] = []
  
  // Transform user to match expected interface for hooks
  const transformedUser = user ? {
    id: user.id,
    companyId: user.companyId,
    email: user.email,
    name: user.name,
    department: Array.isArray(user.department) ? user.department : user.department ? [user.department] : []
  } : null
  
  // Create a minimal user object for useApprovals (only needs id and name)
  const minimalUser = user ? {
    id: user.id,
    name: user.name
  } : null
  
  // Use the requests hook for submission functionality
  const { 
    submitOvertimeRequest,
    checkEmployeeOvertimeEligibility
  } = useOvertimeRequests(transformedUser, setOvertimeRequests, setLoading, setError)
  
  // Use the approvals hook for approval functionality
  const {
    approveOvertimeRequest,
    approveOvertimeRequestAsVP,
    rejectOvertimeRequest
  } = useApprovals(minimalUser, setOvertimeRequests, setLoading, setError)
  
  // Use the data retrieval hook for data retrieval functionality
  const {
    getOvertimeRequestById,
    getUserRequests,
    getPendingRequests,
    getDepartmentRequests,
    getDashboardStats,
    getDepartmentEmployees,
    getAllEmployees
  } = useDataRetrieval(transformedUser, overtimeRequests)
  
  // Create missing functions that are expected by the interface
  const getShifts = () => shifts
  const getAllRequests = () => overtimeRequests
  
  // Use the analytics hook for analytics functionality
  const {
    getOvertimeByDepartment,
    getOvertimeTrend,
    getProductivityMetrics,
    getDepartmentComparison,
    getShiftOvertimeTrends,
    getResidencyData
  } = useAnalyticsData(overtimeRequests, employees)
  
  return {
    overtimeRequests,
    setOvertimeRequests,
    employees,
    shifts,
    loading,
    error,
    refreshRequests,
    user,
    getOvertimeRequestById,
    getUserRequests,
    getPendingRequests,
    getDepartmentRequests,
    getDashboardStats,
    getOvertimeByDepartment,
    getOvertimeTrend,
    getProductivityMetrics,
    getDepartmentComparison,
    getShiftOvertimeTrends,
    getResidencyData,
    getDepartmentEmployees,
    getAllEmployees,
    getShifts,
    checkEmployeeOvertimeEligibility,
    submitOvertimeRequest,
    approveOvertimeRequest,
    approveOvertimeRequestAsVP,
    rejectOvertimeRequest,
    getAllRequests
  }
}
